import React, { useEffect, useState, useMemo } from "react";
import {
  Autocomplete,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { CloseIcon } from "../../helper/Icons";
import { Button, ErrorMessage, Input, Select } from "../../components/Common";
import {
  useGetCityListQuery,
  useGetStateListQuery,
} from "../../services/Location";
import { useMessage } from "../../components/Common/Message/MessageContext";
import { addressTypeList, countryList } from "../../helper";
import {
  useAddUserAddressMutation,
  useUpdateAddressMutation,
} from "../../services/Address";
import { useGetExtraPincodeByCityQuery } from "../../services/SubAdmin";
import { getAllData } from "../../helper/indexDB";

const AddAddress = ({ handleClose, modalOpen, onAddressAdded }) => {
  const { showMessage } = useMessage();
  const [country, setCountry] = useState(1);
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [user, setUser] = useState("");

  const [addressType, setAddressType] = useState("");
  const [errorMessages, setErrorMessages] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedPincode, setSelectedPincode] = useState("");
  const [formValues, setFormValues] = useState({
    house: "",
    building: "",
    address: "",
    landmark: "",
    latitude: "",
    longitude: "",
  });

  const {
    data: stateLst,
    error: stateError,
    isLoading: stateLoading,
  } = useGetStateListQuery();
  const {
    data: cityLst,
    error: cityError,
    isLoading: cityLoading,
  } = useGetCityListQuery(state, { skip: !state });

  const cityList = cityLst?.data;
  const stateList = stateLst?.data;

  // Fetch all pincodes available for the city from extraPincode table
  const { data: extraPincodeData, isLoading: extraPincodeLoading } =
    useGetExtraPincodeByCityQuery(city, {
      skip: !city,
      refetchOnMountOrArgChange: true,
    });

  // All pincodes in selected city from extraPincode table
  const cityPincodes = useMemo(() => {
    if (!city) return [];
    const extraPins = extraPincodeData?.data || [];
    return extraPins.map((pin) => pin.pincode);
  }, [city, extraPincodeData]);

  const handleStateChange = (e) => {
    setState(e.target.value);
    setCity(""); // Reset city when state changes
    setSelectedPincode(""); // Reset pincode when state changes
  };

  useEffect(() => {
    const fetchData = async () => {
      const storedData = await getAllData();
      setUser(storedData?.[0]?.value.id);
    };

    fetchData();
  }, []);

  const handleCountryChange = (e) => setCountry(e.target.value);
  const handleCityChange = (e) => {
    setCity(e.target.value);
    setSelectedPincode(""); // Reset pincode when city changes
  };
  const handleAddressTypeChange = (e) => setAddressType(e.target.value);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const validateForm = () => {
    let errors = {};
    let hasError = false;

    Object.keys(formValues).forEach((key) => {
      if (!formValues[key]) {
        errors[key] = `${
          key.charAt(0).toUpperCase() + key.slice(1)
        } is required`;
        hasError = true;
      }
    });

    if (!addressType) {
      errors.addressType = "Address Type is required";
      hasError = true;
    }
    if (!modalOpen?.isEdit && !country) {
      errors.country = "Country is required";
      hasError = true;
    }
    if (!modalOpen?.isEdit && !state) {
      errors.state = "State is required";
      hasError = true;
    }
    if (!modalOpen?.isEdit && !city) {
      errors.city = "City is required";
      hasError = true;
    }

    // Validate pincode
    if (!selectedPincode) {
      errors.pincode = "Please select a pincode";
      hasError = true;
    }

    setErrorMessages(errors);
    return !hasError;
  };

  const [addAddress] = useAddUserAddressMutation();
  const [updateAddress] = useUpdateAddressMutation();

  const handleSubmit = async () => {
    if (validateForm()) {
      setLoading(true);
      try {
        let response;

        // Prepare data - convert single pincode to array for backend
        const addressData = {
          ...formValues,
          cityId: city.toString(),
          state: state.toString(),
          type: addressType,
          userId: modalOpen?.userId,
          pincode: JSON.stringify([selectedPincode]), // Send as array to backend
        };

        const updatAddressData = {
          ...formValues,
          type: addressType,
          userId: modalOpen?.userId,
          pincode: selectedPincode, // Send single value in edit mode
        };

        if (modalOpen.isEdit) {
          response = await updateAddress({
            body: updatAddressData,
            id: modalOpen?.addressData?.addressId,
            jobId: modalOpen?.jobId,
          });
        } else {
          response = await addAddress(addressData);
        }

        const { status, message } = response?.data;

        if (status) {
          handleClose();
          showMessage("success", message);
          if (onAddressAdded) {
            onAddressAdded();
          }
        } else {
          showMessage("error", message || "An error occurred");
        }
      } catch (error) {
        showMessage("error", "Failed to save the address");
      } finally {
        setLoading(false);
      }
    } else {
      showMessage("error", "Please fill in all required fields");
    }
  };

  // Separate effect to handle city ID lookup when state and cityList are ready
  useEffect(() => {
    if (modalOpen?.addressData && modalOpen?.isEdit && cityList?.length > 0) {
      const cityName = modalOpen.addressData.city;

      // Find city ID from city name
      const cityObj = cityList.find(
        (c) => c.city === cityName || c.name === cityName
      );
      if (cityObj) {
        setCity(cityObj.id.toString());
      }
    }
  }, [modalOpen?.addressData, modalOpen?.isEdit, cityList]);

  useEffect(() => {
    if (modalOpen?.addressData) {
      const { address, buildingName, house, lat, long, landmark, zip } =
        modalOpen.addressData;

      setFormValues({
        address,
        building: buildingName,
        house,
        latitude: lat,
        longitude: long,
        landmark,
      });

      // Set state first (it's already an ID)
      const stateValue = modalOpen.addressData.state || "";
      setState(stateValue.toString());

      // For edit mode, city will be set by the separate effect above when cityList loads
      // For add mode, city should already be empty
      if (!modalOpen.isEdit) {
        setCity("");
      }

      setAddressType(modalOpen.addressData.addressType || "");
      setCountry(modalOpen.addressData.country || 1);

      // Handle pincode - extract single value from array/string
      if (zip) {
        try {
          const parsedZip = typeof zip === "string" ? JSON.parse(zip) : zip;
          setSelectedPincode(
            Array.isArray(parsedZip) ? parsedZip[0] : parsedZip
          );
        } catch {
          setSelectedPincode(zip);
        }
      } else {
        setSelectedPincode("");
      }
    } else {
      setFormValues({
        house: "",
        building: "",
        address: "",
        landmark: "",
        latitude: "",
        longitude: "",
      });
      setCity("");
      setState("");
      setAddressType("");
      setCountry(1);
      setSelectedPincode("");
    }
  }, [modalOpen?.addressData]);

  return (
    <>
      <DialogTitle sx={{ m: 0, p: 2 }} id="Common_modal">
        {modalOpen.isEdit ? "Edit" : "Add"} Address
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <Grid container spacing={2} width={{ sm: "400px" }} paddingY={1}>
          <Grid item xs={12} sm={6}>
            <Input
              labelinput="House No."
              name="house"
              value={formValues.house}
              onChange={handleInputChange}
            />
            {errorMessages.house && (
              <ErrorMessage message={errorMessages.house} />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              labelinput="Building Name"
              name="building"
              value={formValues.building}
              onChange={handleInputChange}
            />
            {errorMessages.building && (
              <ErrorMessage message={errorMessages.building} />
            )}
          </Grid>
          <Grid item xs={12}>
            <Input
              labelinput="Address"
              name="address"
              value={formValues.address}
              onChange={handleInputChange}
            />
            {errorMessages.address && (
              <ErrorMessage message={errorMessages.address} />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              labelinput="Landmark"
              name="landmark"
              value={formValues.landmark}
              onChange={handleInputChange}
            />
            {errorMessages.landmark && (
              <ErrorMessage message={errorMessages.landmark} />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Select
              selectList={addressTypeList}
              labelinput="Address Type"
              value={addressType}
              onChange={handleAddressTypeChange}
            />
            {errorMessages.addressType && (
              <ErrorMessage message={errorMessages.addressType} />
            )}
          </Grid>
          <Grid item xs={12}>
            <Select
              disabled={modalOpen?.isEdit}
              selectList={countryList}
              labelinput="Country"
              value={country}
              onChange={handleCountryChange}
            />
            {errorMessages.country && (
              <ErrorMessage message={errorMessages.country} />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {modalOpen?.isEdit ? (
              <Select
                selectList={stateLoading ? [] : stateList}
                labelinput="State"
                value={state}
                onChange={handleStateChange}
                disabled={modalOpen?.isEdit}
              />
            ) : (
              <>
                <Select
                  selectList={stateLoading ? [] : stateList}
                  labelinput="State"
                  value={state}
                  onChange={handleStateChange}
                />
                {errorMessages.state && (
                  <ErrorMessage message={errorMessages.state} />
                )}
              </>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {modalOpen?.isEdit ? (
              <Select
                selectList={cityLoading ? [] : cityList}
                labelinput="City"
                value={city}
                onChange={handleCityChange}
                disabled={modalOpen?.isEdit}
              />
            ) : (
              <>
                <Select
                  selectList={cityLoading ? [] : cityList}
                  labelinput="City"
                  value={city}
                  onChange={handleCityChange}
                />
                {errorMessages.city && (
                  <ErrorMessage message={errorMessages.city} />
                )}
              </>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography
              fontSize={{ xs: "12px", sm: "13px" }}
              fontWeight={500}
              mb={"2px"}
            >
              Select Pincode
            </Typography>

            <Autocomplete
              id="pincode-select"
              options={extraPincodeLoading ? [] : cityPincodes || []}
              getOptionLabel={(option) => option || ""}
              isOptionEqualToValue={(option, value) => option === value}
              value={selectedPincode || null}
              onChange={(event, newValue) => {
                setSelectedPincode(newValue || "");
                setErrorMessages((prev) => ({ ...prev, pincode: "" }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select pincode"
                  error={!!errorMessages.pincode}
                  helperText={errorMessages.pincode}
                />
              )}
              disabled={!city || extraPincodeLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              labelinput="Latitude"
              type="number"
              name="latitude"
              value={formValues.latitude}
              onChange={handleInputChange}
            />
            {errorMessages.latitude && (
              <ErrorMessage message={errorMessages.latitude} />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              labelinput="Longitude"
              type="number"
              name="longitude"
              value={formValues.longitude}
              onChange={handleInputChange}
            />
            {errorMessages.longitude && (
              <ErrorMessage message={errorMessages.longitude} />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          buttonName={"Cancel"}
          size="small"
          color="white"
          onClick={handleClose}
          disabled={loading}
        />
        <Button
          buttonName={modalOpen.isEdit ? "Update" : "Save"}
          size="small"
          onClick={handleSubmit}
          disabled={loading}
        />
      </DialogActions>
    </>
  );
};

export default AddAddress;
